import { Platform } from "react-native";
import * as FileSystem from "expo-file-system/legacy";

function isLocalUri(uri) {
  return (
    uri.startsWith("file://") ||
    uri.startsWith("content://") ||
    uri.startsWith("ph://") ||
    (uri.startsWith("/") && !uri.startsWith("//"))
  );
}

/** Ensure Android/iOS local paths are usable by expo-file-system. */
function normalizeLocalUri(uri) {
  if (!uri) return uri;
  if (uri.startsWith("/") && !uri.startsWith("//")) {
    return `file://${uri}`;
  }
  return uri;
}

/**
 * Android: upload file bytes via native module (fetch/XHR cannot read file:// URIs).
 * iOS/web: use fetch + blob (works on iOS; web uses http(s) or blob URLs).
 */
async function uploadViaNativeFile(uploadUrl, uri) {
  const fileUri = normalizeLocalUri(uri);

  const info = await FileSystem.getInfoAsync(fileUri);
  if (!info.exists) {
    throw new Error(`Image file not found: ${fileUri}`);
  }

  const result = await FileSystem.uploadAsync(uploadUrl, fileUri, {
    httpMethod: "POST",
    uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
    headers: {
      "Content-Type": "image/jpeg",
    },
  });

  if (result.status < 200 || result.status >= 300) {
    throw new Error(`Failed to upload photo (HTTP ${result.status})`);
  }

  const parsed = JSON.parse(result.body);
  if (!parsed?.storageId) {
    throw new Error("Upload succeeded but no storageId returned");
  }
  return parsed.storageId;
}

async function uploadViaFetchBlob(uploadUrl, uri, { onProgress } = {}) {
  const isRemote = /^https?:\/\//i.test(uri) || uri.startsWith("data:");
  let blob;

  if (isRemote || Platform.OS === "web") {
    const response = await fetch(uri);
    if (!response.ok) {
      throw new Error(`Failed to read image (${response.status})`);
    }
    blob = await response.blob();
  } else {
    // iOS local files: fetch(file://) works
    const response = await fetch(uri);
    if (!response.ok) {
      throw new Error(`Failed to read image (${response.status})`);
    }
    blob = await response.blob();
  }

  if (onProgress) onProgress(10);

  const uploadResponse = await fetch(uploadUrl, {
    method: "POST",
    body: blob,
    headers: { "Content-Type": "image/jpeg" },
  });

  if (!uploadResponse.ok) {
    throw new Error("Failed to upload photo");
  }

  if (onProgress) onProgress(100);
  const { storageId } = await uploadResponse.json();
  return storageId;
}

/**
 * POST image at `uri` to a Convex-generated upload URL; returns storageId.
 */
export async function uploadImageUriToConvex(uploadUrl, uri, { onProgress } = {}) {
  if (!uploadUrl) {
    throw new Error("Missing upload URL");
  }
  if (!uri) {
    throw new Error("Missing image URI");
  }

  if (Platform.OS === "android" && isLocalUri(uri)) {
    if (onProgress) onProgress(10);
    const storageId = await uploadViaNativeFile(uploadUrl, uri);
    if (onProgress) onProgress(100);
    return storageId;
  }

  return uploadViaFetchBlob(uploadUrl, uri, { onProgress });
}
